<template>
  <div class="editSection-container person-edit">
    <modal
      name="confirmDeleteDialog"
      classes="modalBoon"
      click-to-close
      transition="nice-modal-fade"
      :delay="100"
      :width="450"
    >
      <h2>{{ $t('Delete Person?') }}</h2>
      <p>
        {{
          $t('Are you sure you want to delete the person {firstName} {lastName}?', {
            firstName: person.firstName || '',
            lastName: person.lastName || '',
          })
        }}
      </p>
      <p>{{ $t('This action cannot be undone!') }}</p>
      <ul class="modalButtons">
        <li>
          <button class="darkButton" @click="confirmedDeletePerson()">{{ $t('Delete account') }}</button>
        </li>
        <li>
          <button class="lightButton" @click="$modal.hide('confirmDeleteDialog')">{{ $t("Don't Delete") }}</button>
        </li>
      </ul>
    </modal>

    <b-container>
      <b-row>
        <b-col cols="8">
          <h3 class="mt-4">Kontaktkort / Contact card</h3>
        </b-col>

        <b-col class="text-right pt-4">
          <b-button
            v-if="!embedded && person._id && hasSimplePermissions('deletePerson')"
            pill
            variant="outline"
            class="delete-button mr-2 text-uppercase small-button font-weight-bold"
            @click="$modal.show('confirmDeleteDialog', { title: $t('Delete Person?') })"
          >
            <b-icon icon="trash-fill" scale="1.5" class="mr-2" />
            <span>{{ $t('Delete contact') }}</span>
          </b-button>

          <router-link v-if="!embedded" :to="{ name: 'OrganisationOverview' }">
            <b-button
              pill
              v-tooltip="
                !isEmailValid && {
                  content: $t('E-mail will not be saved because it is not in a valid format'),
                  placement: 'bottom',
                  offset: 20,
                  delay: { show: 0, hide: 200 },
                  classes: ['tooltip-bubble'],
                }
              "
              :variant="isEmailValid ? 'primary' : 'danger'"
              class="close-button text-uppercase small-button font-weight-bold"
            >
              {{ $t('Save') }} &amp; {{ $t('Close') }}
            </b-button>
          </router-link>
        </b-col>
      </b-row>

      <br />
      <br />

      <b-row>
        <b-col>
          <b-custom-input
            id="firstName"
            type="text"
            required
            :label="$t('Name')"
            :value="person.firstName"
            @blur="updateSimpleProperty('firstName', $event)"
          />
          <phone-number-field
            :value="person.phone"
            required
            :label="$t('Phone')"
            @input="updateSimpleProperty('phone', $event)"
          />
          <b-custom-input
            id="id06"
            type="text"
            required
            :label="$t('ID-06')"
            :value="person.id06"
            @blur="updateSimpleProperty('id06', $event)"
          />
          <b-custom-input
            id="supervisor"
            type="text"
            required
            :label="$t('Nearest Manager - Name')"
            :value="person.supervisor"
            @blur="updateSimpleProperty('supervisor', $event)"
          />
          <b-custom-input
            id="nextOfKin"
            type="text"
            required
            :label="$t('Nearest Relative - Name')"
            :value="person.nextOfKin"
            @blur="updateSimpleProperty('nextOfKin', $event)"
          />
          <b-custom-input
            id="allergiesAndDiseases"
            type="text"
            required
            :label="$t('Allergies/Diseases')"
            :value="person.allergiesAndDiseases"
            @blur="updateSimpleProperty('allergiesAndDiseases', $event)"
          />
        </b-col>
        <b-col>
          <b-custom-input
            id="lastName"
            type="text"
            required
            :label="$t('Last Name')"
            :value="person.lastName"
            @blur="updateSimpleProperty('lastName', $event)"
          />
          <b-custom-input
            id="email"
            type="text"
            :value="innerEmail"
            required
            :label="$t('E-mail')"
            :class="{ 'invalid-email': !isEmailValid }"
            @blur="updateEmail"
          />
          <!-- <b-custom-input id="company" type="text" required
                          :label="$t('Business')"
                          :value="person.company"
                          @input="updateSimpleProperty('company', $event)" /> -->
          <div class="position-relative">
            <span v-if="!!selectedCompany" class="special-label">{{ $t('Business') }}</span>
            <multiselect
              class="boon-multiselect-v2 mb-2"
              :class="{
                'boon-multiselect-v2__centered': !selectedCompany,
              }"
              :multiple="false"
              :options="companiesList"
              :placeholder="$t('Business')"
              @input="updateSimpleProperty('companyId', $event ? $event._id : '')"
              id="company"
              label="companyName"
              track-by="_id"
              v-model="selectedCompany"
            >
              <div slot="noResult">{{ $t('No matching company found') }}</div>
            </multiselect>
          </div>

          <phone-number-field
            :value="person.supervisorPhone"
            required
            :label="$t('Nearest Manager - Phone Number')"
            @input="updateSimpleProperty('supervisorPhone', $event)"
          />
          <phone-number-field
            :value="person.nextOfKinPhone"
            required
            :label="$t('Nearest Relative - Phone Number')"
            @input="updateSimpleProperty('nextOfKinPhone', $event)"
          />
          <multiselect
            id="languages"
            label="text"
            :value="languages"
            :options="langOptions"
            :placeholder="$t('Language') + '*'"
            trackBy="value"
            multiple
            required
            taggable
            class="boon-multiselect-v2 boon-multiselect-v2__internal"
            @input="changeLanguages"
            @tag="changeLanguages"
          />
        </b-col>
        <b-col>
          <b-card title="Egenskaper" class="characteristics-card">
            <div class="connections-select-container">
              <multiselect
                id="Kopplingar"
                readonly
                :options="taggedProjectList"
                select-label=""
                deselect-label=""
                selected-label=""
                open-direction="bottom"
                class="boon-multiselect connections-select mt-3"
                placeholder="Kopplingar"
              >
                <div slot="noResult">{{ $t('No connections found...') }}</div>

                <template #option="{ option }">
                  <img src="static/icon/gear.png" alt="" />
                  <span class="connections-select-title">{{ option.title }}, {{ option.address.city }}</span>
                  <span class="connections-select-subtitle">{{ option.occupation }}</span>
                </template>
              </multiselect>

              <b-badge v-if="taggedProjectList.length" pill class="connections-count-badge">
                {{ taggedProjectList.length }}
              </b-badge>
            </div>

            <multiselect
              id="occupation"
              taggable
              :multiple="false"
              :value="person.occupation"
              :options="occupations"
              :placeholder="$t('Title') + ' *'"
              :tag-placeholder="$t('Add New')"
              class="mt-3 boon-multiselect-v2 boon-multiselect-v2__internal"
              @input="updateSimpleProperty('occupation', $event)"
              @tag="updateSimpleProperty('occupation', $event)"
            >
              <div slot="noResult">{{ $t('No match found, add new...') }}</div>
            </multiselect>

            <div class="d-flex flex-column justify-content-center align-items-center">
              <s3-file-image v-if="idFile" :s3-key="idFile.s3Key" alt="ID06 file" class="mt-4" />
            </div>
          </b-card>
        </b-col>
      </b-row>

      <person-files :files="personFiles" :loading="personFilesLoading" class="mt-3" />

      <b-card class="my-4 shadow">
        <b-row>
          <b-col>
            <h4>Inloggning / Account</h4>
          </b-col>

          <b-col v-if="innerEmail" cols="4" class="text-right">
            <span v-if="person.userId && !person.username">
              <b-icon icon="arrow-repeat" animation="spin" />
              {{ $t('Removing user credentials from person') }}
            </span>

            <b-button
              v-if="person.userId && person.username"
              class="delete-button text-uppercase font-weight-bold big-button"
              variant="outlined"
              @click="deleteUser"
            >
              {{ $t('Delete account') }}
            </b-button>

            <b-button
              v-if="!person.username && !person.userId && innerEmail"
              pill
              variant="primary"
              :disabled="!isEmailValid"
              class="close-button text-uppercase font-weight-bold big-button"
              @click="createUser"
            >
              {{ $t('Create account') }}
            </b-button>

            <span v-if="!person.userId && person.username">
              <b-icon icon="arrow-repeat" animation="spin" />
              {{ $t('Assigning user credentials to person') }}
            </span>

            <b-button
              v-if="person.username"
              :disabled="person.passwordReset"
              pill
              class="text-uppercase font-weight-bold big-button"
              variant="primary"
              @click="passwordReset"
            >
              {{ $t('Mail new password') }}
            </b-button>

            <div v-if="person.passwordReset && person.username">
              <b-icon icon="arrow-repeat" animation="spin" />
              {{ $t('Sending new password to user') }}
            </div>

            <div style="font-size: 13px" v-if="person.passwordResetDate > '0000-01-01 00:00:00'">
              {{ $t('Mail last sent {fuzzy} ({date})', { fuzzy: passwordResetDateFuzzy, date: passwordResetDate }) }}
            </div>
          </b-col>
        </b-row>

        <hr />

        <b-row v-if="!innerEmail">
          <b-col class="message">
            {{ $t('Add e-mail to contact card to add user.') }}
          </b-col>
        </b-row>

        <b-row v-else-if="!isEmailValid">
          <b-col class="message text-danger">
            {{ $t('E-mail is invalid') }}
          </b-col>
        </b-row>

        <b-row v-if="person.userId">
          <b-col>
            <b-custom-input :label="$t('Username')" type="text" :value="person.username" readonly />
            <b-custom-select
              v-if="isAdmin"
              :label="$t('Competence')"
              label-for="competence-id"
              :options="competenceOptions"
              :disabled="!person.username"
              v-model="role"
            />
          </b-col>
          <b-col>
            <div class="position-relative">
              <span class="special-label">{{ $t('Search by name or number to add') }}</span>
              <multiselect
                id="projects"
                multiple
                class="taggable boon-multiselect-v2 boon-multiselect-v2__show-input"
                track-by="_id"
                :disabled="!person.username"
                :placeholder="$t('Visible projects')"
                :custom-label="projectTitle"
                :options="projectIdList"
                :value="person.projects"
                @input="updateSimpleProperty('projects', $event)"
              />
            </div>

            <div class="auth-info-block">
              <div v-if="person.passwordReset && person.username">
                {{ $t('Sending new password to user') }}
              </div>

              <div v-if="person.passwordResetDate > '0000-01-01 00:00:00'">
                {{ $t('Mail last sent {fuzzy} ({date})', { fuzzy: passwordResetDateFuzzy, date: passwordResetDate }) }}
              </div>

              <div v-if="person.lastLoginDate > '0000-01-01 00:00:00'" :key="4">
                {{ $t('Last Login: {fuzzy} ({date})', { fuzzy: lastLoginDateFuzzy, date: lastLoginDate }) }}
              </div>
              <div v-else :key="5">{{ $t('Never logged in') }}</div>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import moment from 'moment';

import constants from '@/constants';

import { fuzzytime } from '@/common/timeHelpers';
import BCustomInput from '@/components/organisation/BCustomInput';
import BCustomSelect from '@/components/organisation/BCustomSelect';
import PhoneNumberField from '@/components/form/PhoneNumberFieldLegacy';
import S3FileImage from '@/components/common/S3FileImage';
import PersonFiles from '@/components/organisation/PersonFiles';
import { EventBus } from '@/common/eventbus';

export default {
  name: 'EditPerson',

  components: {
    Multiselect,
    BCustomInput,
    BCustomSelect,
    PhoneNumberField,
    S3FileImage,
    PersonFiles,
  },

  props: {
    embedded: {
      type: Boolean,
    },
  },

  data() {
    return {
      person: {},
      innerEmail: null,
      selectedCompany: undefined,
      taggedProjectList: [],
      personFiles: [],
      personFilesLoading: false,
      address: '',
      competenceOptions: [
        { text: this.$t('Administrator'), value: 'admin' },
        { text: this.$t('Project Director'), value: 'projectleader' },
        { text: this.$t('Site Manager'), value: 'sitemanager' },
        { text: this.$t('Site Assistant'), value: 'siteassistant' },
        { text: this.$t('Subcontractor'), value: 'subcontractor' },
        { text: this.$t('Client'), value: 'client' },
      ],
      langOptions: [
        { value: 'en', text: 'English' },
        { value: 'ru', text: 'Русский' },
        { value: 'sv', text: 'Svenska' },
        { value: 'da', text: 'Dansk' },
        { value: 'no', text: 'Norsk' },
        { value: 'fi', text: 'Suomi' },
        { value: 'de', text: 'Deutsch' },
        { value: 'et', text: 'Eesti' },
        { value: 'lv', text: 'Latviešu valoda' },
        { value: 'pl', text: 'Język polski' },
      ],
    };
  },

  computed: {
    ...mapGetters([
      'companies',
      'personByUsername',
      'getCompany',
      'getPerson',
      'hasSimplePermissions',
      'isAdmin',
      'occupations',
      'projectIdList',
      'projectList',
    ]),

    personEmail() {
      return (this.person && this.person.email) || null;
    },

    isEmailValid() {
      return !this.innerEmail || constants.emailRegexp.test(this.innerEmail);
    },

    companiesList() {
      // Preserved original logic. Have no idea why we need to skip first company.
      return this.companies.slice(1);
    },

    passwordResetDate() {
      if (this.person.passwordResetDate.year) {
        const day = this.person.passwordResetDate.dayOfMonth;
        const month = this.person.passwordResetDate.month;
        const year = this.person.passwordResetDate.year;

        return moment(new Date(day + '-' + month + '-' + year)).format('YYYY-MM-DD HH:mm');
      }

      return moment(new Date(this.person.passwordResetDate)).format('YYYY-MM-DD HH:mm');
    },

    passwordResetDateFuzzy() {
      return this.$t(fuzzytime(this.person.passwordResetDate));
    },

    lastLoginDate() {
      return moment(this.person.lastLoginDate).format('YYYY-MM-DD HH:mm');
    },

    lastLoginDateFuzzy() {
      return this.$t(fuzzytime(this.person.lastLoginDate));
    },

    languages() {
      const languages = (this.person && this.person.languages) || [];
      return languages
        .map((lang) => {
          return this.langOptions.find((opt) => opt.value === lang);
        })
        .filter(Boolean);
    },

    role: {
      get() {
        return this.person.role;
      },
      set(value) {
        this.updateSimpleProperty('role', value);
      },
    },

    idFile() {
      return this.personFiles && this.personFiles.find(({ fileName }) => fileName === 'id06CardFile');
    },
  },

  mounted() {
    this.loadPerson(this.$route.params._id);
  },

  methods: {
    ...mapActions(['deletePerson', 'preparePersonEntity', 'savePersonProperties', 'getPersonFiles']),

    changeLanguages(values) {
      this.updateSimpleProperty('languages', values ? values.map(({ value }) => value) : []);
    },

    async updateEmail(value) {
      this.innerEmail = value;
      if (constants.emailRegexp.test(value)) {
        return this.updateSimpleProperty('email', value);
      }
    },

    async updateSimpleProperty(property, newValue) {
      if (this.person[property] === newValue) {
        return;
      }

      const oldId = this.person._id;
      this.person = await this.savePersonProperties({
        updatePerson: this.person,
        newPersonProperties: { [property]: newValue },
      });

      if (oldId !== this.person._id) {
        this.$emit('input', this.person._id);
        this.findProjectsAssignedToPerson();
      }
    },

    confirmedDeletePerson() {
      this.deletePerson({ person: this.person });
      this.$router.push({ name: 'OrganisationOverview' });
    },

    async loadPerson(personId) {
      if (personId && !this.embedded) {
        this.person = this.getPerson(personId);

        if (!this.person.hasOwnProperty('lastName')) {
          console.debug('EditPerson::loadPerson Personlist not yet populated.');
          setTimeout(() => {
            this.loadPerson(personId);
          }, 200);
          return;
        }

        if (this.person.companyId) {
          this.selectedCompany = this.getCompany(this.person.companyId);
        }
      } else {
        this.person = await this.preparePersonEntity();
        this.selectedCompany = undefined;
      }

      this.findProjectsAssignedToPerson();
      this.loadPersonFiles();
    },

    createUser() {
      if (!this.innerEmail || !this.isEmailValid) {
        EventBus.$emit('showMessageToUser', { message: this.$t('Invalid Email'), type: 'error', duration: 3000 });
        return;
      }

      if (this.personByUsername(this.innerEmail)) {
        EventBus.$emit('showMessageToUser', {
          message: this.$t('Person with this email already exists'),
          type: 'error',
          duration: 3000,
        });
        return;
      }

      this.savePersonProperties({
        updatePerson: this.person,
        newPersonProperties: {
          role: 'subcontractor',
          username: this.innerEmail,
        },
      });
    },

    projectTitle(project) {
      const realProject = this.projectIdList.find(({ _id }) => _id === project._id);
      return realProject ? realProject.title : '?';
    },

    deleteUser() {
      this.savePersonProperties({
        updatePerson: this.person,
        newPersonProperties: {
          username: '',
        },
      });
    },

    passwordReset() {
      this.savePersonProperties({
        updatePerson: this.person,
        newPersonProperties: {
          passwordReset: true,
          passwordResetDate: '0000-00-00 00:00:00',
        },
      });
    },

    findProjectsAssignedToPerson() {
      this.taggedProjectList = [];
      if (!this.person || !this.person._id) {
        return;
      }

      // Compile a list of all projects this person is assigned to.
      this.projectList.forEach(({ title, address, projectManagers, projectSubcontractors }) => {
        projectManagers.forEach(({ personId, occupation }) => {
          if (personId === this.person._id) {
            this.taggedProjectList.push({ title, address, occupation });
          }
        });

        projectSubcontractors.forEach(({ personId, occupation }) => {
          if (personId === this.person._id) {
            this.taggedProjectList.push({ title, address, occupation });
          }
        });
      });
    },

    async loadPersonFiles() {
      if (!this.person || !this.person._id) {
        this.personFiles = [];
        return;
      }

      this.personFilesLoading = true;
      const doc = await this.getPersonFiles(this.person._id);
      this.personFiles = (doc && doc.filesLinks) || [];
      this.personFilesLoading = false;
    },
  },

  watch: {
    $route() {
      this.loadPerson(this.$route.params._id);
    },

    personEmail: {
      immediate: true,
      handler(next) {
        this.innerEmail = next;
      },
    },
  },
};
</script>

<style scoped>
/* Add the decaration on top */
@font-face {
  /* font-family: 'DIN Alternate Bold'; */
  /* font-style: normal; */
  /* font-weight: normal; */
  /* src: local('DIN Alternate Bold'), url('/static/fonts/DINAlternate-Bold.woff') format('woff'); */

  font-family: 'DIN Alternate Bold';
  /* src: url('location.eot'); */
  src:
      /* url('location.eot#iefix') format('embedded-opentype'), */ url('/static/fonts/DINAlternate-Bold.woff')
      format('woff'),
    url('/static/fonts/DINAlternate-Bold.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}
@font-face {
  font-family: 'DIN-Light';
  src: url('/static/fonts/DINLight.woff') format('woff'); /* Modern Browsers */
  font-weight: normal;
  font-style: normal;
}

.auth-info-block {
  position: absolute;
  bottom: 15px;
  font-size: 12px;
  color: grey;
}

.special-label {
  display: block;
  /* margin-bottom: -15px; */
  z-index: 10;
  position: absolute;
  line-height: 1rem;
  font-size: 12px;
  color: #777;
  left: 17px;
  top: 8px;
}

.characteristics-card {
  height: calc(100% - 0.5rem);
}

.small-button {
  font-size: 0.85rem;
}

.big-button {
  font-size: 0.9rem;
}

.delete-button {
  display: inline-flex;
  flex-wrap: nowrap;
  flex-direction: row;
  align-items: center;
  border-width: 0;
  color: var(--btn-primary-color);
}

.close-button {
  padding: 0.375rem 2rem;
}

.person-edit .invalid-email >>> .form-control {
  border-color: #f0504a;
}

.person-edit .invalid-email >>> .form-control:focus {
  box-shadow: 0 0 0 0.25rem rgba(240, 80, 74, 0.25);
}

.editSection-container {
  display: flex;
  flex-direction: column;
}

.person-edit .content {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 75vh;
  overflow-y: scroll;
  overflow-x: hidden;
}
.person-edit .boon-form-container {
  display: flex;
  flex-direction: row;
  padding: 20px 0;
  margin: 0 30px;
  /* height: 100vh; */
}
.person-edit .boon-form-container .left-sile,
.person-edit .boon-form-container .right-side {
  width: 48%;
  flex-direction: column !important;
  padding-left: 0 !important;
  margin-left: 0 !important;
  justify-content: unset;
  margin-top: 0;
}
.bar {
  border: 1px solid #051623;
  margin: 0 30px;
}
.person-edit .boon-fields-container {
  display: flex;
  /* flex-flow: row nowrap; */
  padding: 5px 0;
  justify-content: space-between;
  margin-top: 20px;
}

.person-edit .boon-input-container {
  margin: 7px 0 !important;
  width: calc(50% - 28px);
}

.right-side .half {
  width: 100% !important;
}

.fourth {
  width: calc(25% - 28px);
}

.boon-fields-container.adress-fields {
  flex-flow: row wrap;
}
.top-title {
  border-bottom: 2px solid #051623;
  padding-bottom: 15px;
  font-family: 'DIN Alternate Bold';
  font-weight: normal;
  font-size: 40px;
}
.name-fields {
  padding-top: 25px;
}
.contact-fiels {
  padding-top: 25px;
}
.right-side .darkButton {
  font-family: 'DIN Alternate Bold';
  margin: 0;
  padding: 10px;
  margin-bottom: 10px;
  justify-content: center;
  font-weight: 500;
  font-size: 20px;
}
.bottom-title {
  border-top: 2px solid #051623;
  margin-top: 30px;
  padding-top: 20px;
  margin-bottom: 15px;
  font-family: 'DIN Alternate Bold';
  font-weight: normal;
  font-size: 40px;
}
#authorization label {
  font-family: 'DIN Alternate Bold';
  color: #71737f;
}
#authorization label.selected {
  color: #000;
  text-decoration: underline;
}
#authorization label:hover {
  color: #000;
  text-decoration: underline;
}
/* .right-side .bottom-title{
  margin-top: 10px;
} */

.connections-select >>> .multiselect__content .multiselect__option {
  /** Overriding our default background color for multiselect menu. */
  background: #ffffff;
}

.connections-select >>> .multiselect__content .multiselect__option img {
  /** Styles are messed up. Had to copy color from OrganisationOverview.vue to override. */
  color: #000000;
  width: 20px;
  margin-right: 10px;
}

.connections-select >>> .multiselect__content .multiselect__option .connections-select-title {
  color: #000000;
  font-weight: 600;
  margin-bottom: 5px;
  font-family: 'DIN Alternate Bold';
}

.connections-select >>> .multiselect__content .multiselect__option .connections-select-subtitle {
  color: #000000;
  font-weight: 300;
  font-family: 'DIN-Light';
}

.connections-select-container {
  position: relative;
}

.connections-count-badge {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(-3rem, -50%);
  padding: 0.45em 0.65em;
  background-color: #e4e4e4;
  color: #212529;
  border-radius: 50%;
}

.person-edit #project-list {
  width: 100%;
  position: relative;
  margin: 0;
  height: 140px;
}
.person-edit .message {
  width: 76%;
  display: flex;
  flex-direction: row;
  height: 5rem;
  justify-content: center;
  align-items: center;
  font-size: 36px;
  text-align: center;
  font-weight: 400;
  font-family: 'DIN Alternate Bold';
}
.workplace-person-add {
  background: #fff;
  color: #000;
}
.workplace-person-add .top-title {
  color: #000;
}
.workplace-person-add .boon-input {
  color: #000;
}
.workplace-person-add .boon-form-container .connections,
.workplace-person-add .boon-form-container .right-side .message {
  display: none;
}
@media only screen and (max-width: 1280px) {
  .workplace-person-add .boon-form-container {
    flex-direction: column;
  }
  .workplace-person-add .boon-form-container .bar {
    display: none;
  }
  .workplace-person-add .boon-form-container .left-sile,
  .workplace-person-add .boon-form-container .right-side {
    width: 100%;
  }
  .workplace-person-add .boon-form-container .right-side {
    margin-top: 20px;
  }
}
@media only screen and (max-width: 1024px) {
  .workplace-person-add .top-title {
    font-size: 24px;
  }
  .workplace-person-add .boon-fields-container {
    flex-direction: column;
    margin-top: 10px;
  }
  .workplace-person-add .boon-input-container {
    margin: 7px 0 15px !important;
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/organisation/EditPerson.vue